// Internationalisation. Langues actives : français + anglais (pivot/fallback).
// Ajouter une langue = étendre Lang, LANGS et UI ici, puis les listes de messages
// dans messages.ts. La détection prend l'override stocké, sinon la langue du
// navigateur (et plus tard Telegram), sinon l'anglais.

export type Lang = "fr" | "en" | "ru" | "es" | "pt";

export const LANGS: Lang[] = ["fr", "en", "ru", "es", "pt"];

export interface UIStrings {
  pickTag: string; // sous-titre du sélecteur de style
  pickHint: string; // aide en bas de l'écran d'accueil
  language: string; // libellé de la ligne langue
  instruction: string; // consigne sous le canvas
  score: string;
  misses: string;
  best: string;
  perfect: string;
  good: string;
  ok: string;
  timeUp: string;
  newRecord: string;
  playAgain: string;
  changeStyle: string; // bouton fin de partie → retour au menu principal
  overHint: string; // aide sur l'écran de fin
  menuPlay: string; // entrée HUB → styles
  menuCustom: string; // entrée HUB → personnaliser
  customNose: string; // champ texte nez
  customHand: string; // champ texte doigts
  typeName: string; // invite de saisie
  resume: string; // pause → reprendre
  restart: string; // pause → recommencer
  quitMenu: string; // pause → quitter vers le menu
  menuProfile: string; // entrée HUB → profil/stats
  menuBoard: string; // entrée HUB → classement
  menuShop: string; // entrée HUB → boutique
  menuSound: string; // entrée HUB → son on/off
  menuDaily: string; // entrée HUB → objectifs du jour
  pickMode: string; // tag de l'écran de choix du mode (avant la partie)
  secModes: string; // section boutique : modes
  secStyles: string; // section boutique : styles
  secExtras: string; // section boutique : extras
  free: string; // prix d'un article gratuit (0⭐)
  unlock: string; // bouton "débloquer" (in-écran personnaliser)
  statTotal: string; // libellé "cumul des scores"
  statGames: string; // libellé "parties jouées"
  statRank: string; // libellé "rang"
  boardEmpty: string; // classement vide
  netError: string; // données indisponibles (réseau/auth)
  periodDay: string; // classement du jour
  periodWeek: string; // classement de la semaine
  periodAll: string; // classement de tous les temps
  shareCta: string; // bouton partager (MainButton fin de partie)
  shareText: string; // message de partage, {n} = score
  howTo: string; // titre du mini-tuto (1er lancement)
  continueCta: string; // bouton « Continuer » (fin de partie, payant)
  streakTitle: string; // popup daily streak, {n} = jours consécutifs
  menuInvite: string; // entrée HUB → inviter un ami (parrainage)
  inviteText: string; // message envoyé avec le lien de parrainage
  secBoosters: string; // section boutique + écran d'activation
  boosterNone: string; // « jouer sans booster »
  boostSlow: string; // booster slow-mo
  boostDouble: string; // booster pièces ×2
  boostShield: string; // booster bouclier (1 raté pardonné)
  dailyDone: string; // tous les défis du jour réclamés
  claim: string; // bouton « réclamer » sur un défi accompli
  noCoins: string; // pas assez de pièces (achat refusé)
  secCoins: string; // section boutique : packs de pièces
  won: string; // titre fin de partie : objectif de série atteint
  noLives: string; // titre fin de partie : plus de vies
  modeNames: Record<string, string>; // labels des modes, par clé (modes.ts)
  chestOpen: string; // coffre du jour à ouvrir (écran de fin)
  chestNothing: string; // lot vide
  chestModeWin: string; // jackpot : mode offert, {name} = nom du mode
}

export const UI: Record<Lang, UIStrings> = {
  fr: {
    pickTag: "- CHOISIS TON STYLE -",
    pickHint: "▲▼ · A : OK",
    language: "LANGUE",
    instruction: "Clique (ou Espace) quand le doigt est sous le nez 👃",
    score: "Score",
    misses: "Ratés",
    best: "Record",
    perfect: "PARFAIT",
    good: "BIEN",
    ok: "OK",
    timeUp: "TEMPS ÉCOULÉ",
    newRecord: "★ NOUVEAU RECORD ★",
    playAgain: "REJOUER",
    changeStyle: "MENU",
    overHint: "▲▼ · Espace / clic",
    menuPlay: "JOUER",
    menuCustom: "PERSONNALISER",
    customNose: "NEZ",
    customHand: "DOIGTS",
    typeName: "Tape un nom",
    resume: "REPRENDRE",
    restart: "RECOMMENCER",
    quitMenu: "QUITTER",
    menuProfile: "PROFIL",
    menuBoard: "CLASSEMENT",
    menuShop: "BOUTIQUE",
    menuSound: "SON",
    menuDaily: "DÉFIS",
    pickMode: "- CHOISIS LE MODE -",
    secModes: "MODES",
    secStyles: "STYLES",
    secExtras: "EXTRAS",
    free: "GRATUIT",
    unlock: "DÉBLOQUER",
    statTotal: "Total",
    statGames: "Parties",
    statRank: "Rang",
    boardEmpty: "Aucun score",
    netError: "Hors-ligne",
    periodDay: "JOUR",
    periodWeek: "SEMAINE",
    periodAll: "TOUT",
    shareCta: "PARTAGER",
    shareText: "J'ai gratté {n} à The Big Dig 👃 Bats-moi !",
    howTo: "COMMENT JOUER",
    continueCta: "CONTINUER",
    streakTitle: "SÉRIE DE {n} JOURS !",
    menuInvite: "INVITER",
    inviteText: "Viens creuser avec moi sur The Big Dig 👃 On gagne tous les deux des pièces !",
    secBoosters: "BOOSTERS",
    boosterNone: "SANS BOOSTER",
    boostSlow: "SLOW-MO",
    boostDouble: "PIÈCES ×2",
    boostShield: "BOUCLIER",
    dailyDone: "Tout est fait ! Reviens demain 👃",
    claim: "RÉCLAMER",
    noCoins: "Pas assez de pièces !",
    secCoins: "PIÈCES",
    won: "RÉUSSI !",
    noLives: "PLUS DE VIES",
    modeNames: {
      classic: "Classique",
      sprint: "Sprint 15s",
      survie: "Survie 3 vies",
      serie: "Série de 12",
      sniper: "Sniper",
      defile: "Défilé",
      nezmobile: "Nez mobile",
      nezpivot: "Nez pivotant",
    },
    chestOpen: "🎁 COFFRE DU JOUR : OUVRE !",
    chestNothing: "💨 Le nez était vide…",
    chestModeWin: "MODE {name} OFFERT !",
  },
  en: {
    pickTag: "- PICK YOUR STYLE -",
    pickHint: "▲▼ · A : OK",
    language: "LANGUAGE",
    instruction: "Click (or Space) when the finger is under the nose 👃",
    score: "Score",
    misses: "Misses",
    best: "Best",
    perfect: "PERFECT",
    good: "GOOD",
    ok: "OK",
    timeUp: "TIME'S UP",
    newRecord: "★ NEW RECORD ★",
    playAgain: "PLAY AGAIN",
    changeStyle: "MENU",
    overHint: "▲▼ · Space / click",
    menuPlay: "PLAY",
    menuCustom: "CUSTOMIZE",
    customNose: "NOSE",
    customHand: "FINGERS",
    typeName: "Type a name",
    resume: "RESUME",
    restart: "RESTART",
    quitMenu: "QUIT",
    menuProfile: "PROFILE",
    menuBoard: "LEADERBOARD",
    menuShop: "SHOP",
    menuSound: "SOUND",
    menuDaily: "DAILY",
    pickMode: "- PICK A MODE -",
    secModes: "MODES",
    secStyles: "STYLES",
    secExtras: "EXTRAS",
    free: "FREE",
    unlock: "UNLOCK",
    statTotal: "Total",
    statGames: "Games",
    statRank: "Rank",
    boardEmpty: "No scores yet",
    netError: "Offline",
    periodDay: "DAY",
    periodWeek: "WEEK",
    periodAll: "ALL",
    shareCta: "SHARE",
    shareText: "I dug {n} in The Big Dig 👃 Beat me!",
    howTo: "HOW TO PLAY",
    continueCta: "CONTINUE",
    streakTitle: "{n}-DAY STREAK!",
    menuInvite: "INVITE",
    inviteText: "Come dig with me in The Big Dig 👃 We both earn coins!",
    secBoosters: "BOOSTERS",
    boosterNone: "NO BOOSTER",
    boostSlow: "SLOW-MO",
    boostDouble: "COINS ×2",
    boostShield: "SHIELD",
    dailyDone: "All done! Come back tomorrow 👃",
    claim: "CLAIM",
    noCoins: "Not enough coins!",
    secCoins: "COINS",
    won: "YOU DID IT!",
    noLives: "OUT OF LIVES",
    modeNames: {
      classic: "Classic",
      sprint: "Sprint 15s",
      survie: "Survival ×3",
      serie: "Streak of 12",
      sniper: "Sniper",
      defile: "Conveyor",
      nezmobile: "Moving nose",
      nezpivot: "Tilting nose",
    },
    chestOpen: "🎁 DAILY CHEST: OPEN IT!",
    chestNothing: "💨 The nose was empty…",
    chestModeWin: "{name} MODE UNLOCKED!",
  },
  ru: {
    pickTag: "- ВЫБЕРИ СТИЛЬ -",
    pickHint: "▲▼ · A : OK",
    language: "ЯЗЫК",
    instruction: "Жми (или Пробел), когда палец под носом 👃",
    score: "Очки",
    misses: "Промахи",
    best: "Рекорд",
    perfect: "ИДЕАЛЬНО",
    good: "НЕПЛОХО",
    ok: "ОК",
    timeUp: "ВРЕМЯ ВЫШЛО",
    newRecord: "★ НОВЫЙ РЕКОРД ★",
    playAgain: "ЕЩЁ РАЗ",
    changeStyle: "МЕНЮ",
    overHint: "▲▼ · Пробел / клик",
    menuPlay: "ИГРАТЬ",
    menuCustom: "НАСТРОИТЬ",
    customNose: "НОС",
    customHand: "ПАЛЬЦЫ",
    typeName: "Введите имя",
    resume: "ПРОДОЛЖИТЬ",
    restart: "ЗАНОВО",
    quitMenu: "ВЫЙТИ",
    menuProfile: "ПРОФИЛЬ",
    menuBoard: "РЕЙТИНГ",
    menuShop: "МАГАЗИН",
    menuSound: "ЗВУК",
    menuDaily: "ЗАДАНИЯ",
    pickMode: "- ВЫБЕРИ РЕЖИМ -",
    secModes: "РЕЖИМЫ",
    secStyles: "СТИЛИ",
    secExtras: "РАЗНОЕ",
    free: "БЕСПЛАТНО",
    unlock: "РАЗБЛОКИРОВАТЬ",
    statTotal: "Всего",
    statGames: "Игры",
    statRank: "Ранг",
    boardEmpty: "Пока без очков",
    netError: "Не в сети",
    periodDay: "ДЕНЬ",
    periodWeek: "НЕДЕЛЯ",
    periodAll: "ВСЁ",
    shareCta: "ПОДЕЛИТЬСЯ",
    shareText: "Я выбил {n} в The Big Dig 👃 Побей меня!",
    howTo: "КАК ИГРАТЬ",
    continueCta: "ПРОДОЛЖИТЬ",
    streakTitle: "{n} ДН. ПОДРЯД!",
    menuInvite: "ПРИГЛАСИТЬ",
    inviteText: "Идём ковырять со мной в The Big Dig 👃 Мы оба получим монеты!",
    secBoosters: "БУСТЕРЫ",
    boosterNone: "БЕЗ БУСТЕРА",
    boostSlow: "СЛОУ-МО",
    boostDouble: "МОНЕТЫ ×2",
    boostShield: "ЩИТ",
    dailyDone: "Всё выполнено! Возвращайся завтра 👃",
    claim: "ЗАБРАТЬ",
    noCoins: "Не хватает монет!",
    secCoins: "МОНЕТЫ",
    won: "ПОЛУЧИЛОСЬ!",
    noLives: "ЖИЗНИ КОНЧИЛИСЬ",
    modeNames: {
      classic: "Классика",
      sprint: "Спринт 15с",
      survie: "Выживание ×3",
      serie: "Серия из 12",
      sniper: "Снайпер",
      defile: "Конвейер",
      nezmobile: "Живой нос",
      nezpivot: "Нос-качели",
    },
    chestOpen: "🎁 СУНДУК ДНЯ: ОТКРОЙ!",
    chestNothing: "💨 Нос был пуст…",
    chestModeWin: "РЕЖИМ {name} В ПОДАРОК!",
  },
  es: {
    pickTag: "- ELIGE TU ESTILO -",
    pickHint: "▲▼ · A : OK",
    language: "IDIOMA",
    instruction: "Pulsa (o Espacio) cuando el dedo esté bajo la nariz 👃",
    score: "Puntos",
    misses: "Fallos",
    best: "Récord",
    perfect: "PERFECTO",
    good: "BIEN",
    ok: "OK",
    timeUp: "TIEMPO AGOTADO",
    newRecord: "★ NUEVO RÉCORD ★",
    playAgain: "OTRA VEZ",
    changeStyle: "MENÚ",
    overHint: "▲▼ · Espacio / clic",
    menuPlay: "JUGAR",
    menuCustom: "PERSONALIZAR",
    customNose: "NARIZ",
    customHand: "DEDOS",
    typeName: "Escribe un nombre",
    resume: "REANUDAR",
    restart: "REINICIAR",
    quitMenu: "SALIR",
    menuProfile: "PERFIL",
    menuBoard: "CLASIFICACIÓN",
    menuShop: "TIENDA",
    menuSound: "SONIDO",
    menuDaily: "RETOS",
    pickMode: "- ELIGE EL MODO -",
    secModes: "MODOS",
    secStyles: "ESTILOS",
    secExtras: "EXTRAS",
    free: "GRATIS",
    unlock: "DESBLOQUEAR",
    statTotal: "Total",
    statGames: "Partidas",
    statRank: "Puesto",
    boardEmpty: "Sin puntuaciones",
    netError: "Sin conexión",
    periodDay: "DÍA",
    periodWeek: "SEMANA",
    periodAll: "TODO",
    shareCta: "COMPARTIR",
    shareText: "¡Hice {n} en The Big Dig 👃 Supérame!",
    howTo: "CÓMO JUGAR",
    continueCta: "CONTINUAR",
    streakTitle: "¡RACHA DE {n} DÍAS!",
    menuInvite: "INVITAR",
    inviteText: "¡Ven a hurgar conmigo en The Big Dig 👃 Los dos ganamos monedas!",
    secBoosters: "BOOSTERS",
    boosterNone: "SIN BOOSTER",
    boostSlow: "SLOW-MO",
    boostDouble: "MONEDAS ×2",
    boostShield: "ESCUDO",
    dailyDone: "¡Todo hecho! Vuelve mañana 👃",
    claim: "RECLAMAR",
    noCoins: "¡Faltan monedas!",
    secCoins: "MONEDAS",
    won: "¡LO LOGRASTE!",
    noLives: "SIN VIDAS",
    modeNames: {
      classic: "Clásico",
      sprint: "Sprint 15s",
      survie: "Supervivencia ×3",
      serie: "Racha de 12",
      sniper: "Sniper",
      defile: "Desfile",
      nezmobile: "Nariz móvil",
      nezpivot: "Nariz giratoria",
    },
    chestOpen: "🎁 COFRE DEL DÍA: ¡ÁBRELO!",
    chestNothing: "💨 La nariz estaba vacía…",
    chestModeWin: "¡MODO {name} GRATIS!",
  },
  pt: {
    pickTag: "- ESCOLHA O ESTILO -",
    pickHint: "▲▼ · A : OK",
    language: "IDIOMA",
    instruction: "Toque (ou Espaço) quando o dedo estiver sob o nariz 👃",
    score: "Pontos",
    misses: "Erros",
    best: "Recorde",
    perfect: "PERFEITO",
    good: "BOM",
    ok: "OK",
    timeUp: "TEMPO ESGOTADO",
    newRecord: "★ NOVO RECORDE ★",
    playAgain: "DE NOVO",
    changeStyle: "MENU",
    overHint: "▲▼ · Espaço / clique",
    menuPlay: "JOGAR",
    menuCustom: "PERSONALIZAR",
    customNose: "NARIZ",
    customHand: "DEDOS",
    typeName: "Digite um nome",
    resume: "RETOMAR",
    restart: "RECOMEÇAR",
    quitMenu: "SAIR",
    menuProfile: "PERFIL",
    menuBoard: "RANKING",
    menuShop: "LOJA",
    menuSound: "SOM",
    menuDaily: "DESAFIOS",
    pickMode: "- ESCOLHA O MODO -",
    secModes: "MODOS",
    secStyles: "ESTILOS",
    secExtras: "EXTRAS",
    free: "GRÁTIS",
    unlock: "DESBLOQUEAR",
    statTotal: "Total",
    statGames: "Partidas",
    statRank: "Posição",
    boardEmpty: "Sem pontuações",
    netError: "Offline",
    periodDay: "DIA",
    periodWeek: "SEMANA",
    periodAll: "TUDO",
    shareCta: "COMPARTILHAR",
    shareText: "Fiz {n} no The Big Dig 👃 Me supere!",
    howTo: "COMO JOGAR",
    continueCta: "CONTINUAR",
    streakTitle: "SEQUÊNCIA DE {n} DIAS!",
    menuInvite: "CONVIDAR",
    inviteText: "Vem cutucar comigo no The Big Dig 👃 Nós dois ganhamos moedas!",
    secBoosters: "BOOSTERS",
    boosterNone: "SEM BOOSTER",
    boostSlow: "SLOW-MO",
    boostDouble: "MOEDAS ×2",
    boostShield: "ESCUDO",
    dailyDone: "Tudo feito! Volte amanhã 👃",
    claim: "RESGATAR",
    noCoins: "Moedas insuficientes!",
    secCoins: "MOEDAS",
    won: "CONSEGUIU!",
    noLives: "SEM VIDAS",
    modeNames: {
      classic: "Clássico",
      sprint: "Sprint 15s",
      survie: "Sobrevivência ×3",
      serie: "Sequência de 12",
      sniper: "Sniper",
      defile: "Desfile",
      nezmobile: "Nariz móvel",
      nezpivot: "Nariz giratório",
    },
    chestOpen: "🎁 BAÚ DO DIA: ABRA!",
    chestNothing: "💨 O nariz estava vazio…",
    chestModeWin: "MODO {name} DE GRAÇA!",
  },
};

const LANG_KEY = "the-big-dig.lang";

function isLang(v: unknown): v is Lang {
  return v === "fr" || v === "en" || v === "ru" || v === "es" || v === "pt";
}

/** Langue active : override stocké → langue du navigateur → anglais. */
export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* stockage indisponible */
  }
  // Langue Telegram (si Mini App) puis langue du navigateur.
  const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
  const src = (tgLang || navigator.language || "en").toLowerCase();
  if (src.startsWith("fr")) return "fr";
  if (src.startsWith("ru")) return "ru";
  if (src.startsWith("es")) return "es";
  if (src.startsWith("pt")) return "pt";
  return "en";
}

export function saveLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* stockage indisponible : la langue reste en mémoire */
  }
}

/** Langue suivante dans le cycle (pour basculer FR ↔ EN). */
export function nextLang(lang: Lang): Lang {
  return LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length]!;
}
